import {
  ConnectionQuality,
  Room,
  RoomEvent,
  Track,
  VideoPreset,
  type LocalParticipant,
  type Participant,
  type RemoteParticipant,
  type TrackPublication,
  type VideoCodec,
} from 'livekit-client';

import { audioPresetFor, captureOptions, type AudioMode } from './audio';
import { layerBudgetFor, videoProfileFor, type VideoMode } from './video';
import { readHostFlag } from './roomState';

export { readRoomSettings, screenShareHint, type RoomSettings } from './roomState';

/**
 * Публикуется ли этот кодек через SVC.
 *
 * livekit-client сам включает `L3T3_KEY` для VP9 и AV1 и в этом случае
 * игнорирует `simulcast`. Разница важна для битрейта: у SVC одна запись
 * кодирования на все слои, у остальных — по записи на слой.
 */
const isSvc = (codec: VideoCodec) => codec === 'vp9' || codec === 'av1';

/**
 * Настройки публикации видео для выбранного режима качества.
 *
 * Вынесены отдельно, потому что применяются дважды: при создании комнаты и
 * при смене режима во время разговора — тогда их надо положить в
 * `room.options.publishDefaults`, чтобы следующая перепубликация (например,
 * после выключения и включения камеры) взяла уже новые значения.
 */
export function videoPublishOptions(codec: VideoCodec, mode: VideoMode) {
  const profile = videoProfileFor(mode);
  const budget = layerBudgetFor(mode);
  const svc = isSvc(codec);

  return {
    // У SVC запись кодирования одна и её потолок покрывает все слои сразу,
    // поэтому туда идёт весь бюджет. У simulcast записей три, и верхнему
    // слою достаётся только его доля — остальное уходит в нижние.
    videoEncoding: {
      maxBitrate: svc ? profile.maxBitrate : budget.top,
      maxFramerate: 30,
    },
    // Для SVC этот список игнорируется — слои строит сам кодек.
    videoSimulcastLayers: budget.lower.map(
      (l) => new VideoPreset(l.width, l.height, l.maxBitrate, l.maxFramerate),
    ),
    degradationPreference: profile.degradation,
    screenShareEncoding: {
      maxBitrate: profile.screenShareBitrate,
      maxFramerate: 30,
    },
  };
}

/**
 * Конфигурация публикации.
 *
 * ── Почему именно такие значения ────────────────────────────────────
 *
 * Съёмка в 720p. Понижение до 540p ради экономии процессора оказалось
 * ошибкой: тормоза давал не размер кадра, а `backupCodec`, заставлявший
 * кодировать поток дважды. Его больше нет, а разрешение вернулось.
 *
 * `videoSimulcastLayers` не задаём вручную: LiveKit сам построит слои
 * от фактического разрешения. Прежний жёсткий список [180p, 360p] мешал
 * ему выбрать разумную середину.
 *
 * Потолок битрейта задан явно и берётся из выбранного режима качества.
 * Автоподбор LiveKit тут не годится: VP9 и AV1 публикуются через SVC
 * (`L3T3_KEY`, библиотека включает его сама), а для SVC `maxBitrate`
 * считается **сразу за все три пространственных слоя**. По таблице
 * выходило 800 кбит/с, да ещё с множителем 0.85 для VP9, — на верхний
 * слой оставалось около 450 кбит/с. Отсюда и была «картинка в пикселях»
 * при полностью свободном канале.
 */
export function createRoom(options: {
  cameraId?: string;
  micId?: string;
  videoCodec: VideoCodec;
  videoMode: VideoMode;
  audioMode: AudioMode;
  noiseSuppression: boolean;
}): Room {
  const deviceIds = options;
  return new Room({
    adaptiveStream: {
      /*
       * Размер плитки считаем в физических пикселях экрана.
       *
       * По умолчанию livekit-client берёт множитель 1 везде, где плотность
       * экрана не больше двух, — а это все обычные ретина-ноутбуки, половина
       * Android-телефонов и все iPad. Плитка шириной 988 CSS-пикселей просила
       * видео шириной 988, получала слой 960×540 и растягивала его на 1976
       * физических точек. Никакой битрейт этого не исправит: мыло берётся
       * не из сжатия, а из растягивания.
       *
       * `'screen'` — это ровно «запросить столько точек, сколько экран
       * действительно покажет». Не больше (иначе платим за пиксели, которые
       * некуда вывести) и не меньше (иначе растягиваем). Фиксированная
       * двойка, стоявшая здесь раньше, на обычном мониторе просила вдвое
       * больше нужного, а на телефоне с плотностью 3 — всё ещё недобирала.
       *
       * Трафик: худший случай — шесть встреч по шесть человек, все смотрят
       * друг друга во весь экран. Исходящий поток около 360 Мбит/с в режиме
       * «Авто» и до 550 Мбит/с, если все выберут «Высокое». Канал сервера —
       * гигабит, запас двукратный.
       */
      pixelDensity: 'screen',
    },
    // Сервер не пересылает слои, которые никто не смотрит.
    dynacast: true,

    videoCaptureDefaults: {
      deviceId: deviceIds.cameraId || undefined,
      // Разрешение задаётся режимом качества: 1080p только в «Высоком».
      resolution: videoProfileFor(options.videoMode).resolution,
    },
    audioCaptureDefaults: captureOptions(options.noiseSuppression, deviceIds.micId),

    publishDefaults: {
      videoCodec: options.videoCodec,
      simulcast: true,
      ...videoPublishOptions(options.videoCodec, options.videoMode),
      audioPreset: audioPresetFor(options.audioMode),
      // DTX молчит в паузах речи, RED защищает от потерь. Оба нужны всегда:
      // DTX убирает из канала тишину неговорящих — на встрече это почти все
      // и почти всё время, — а RED вытягивает звук при потере пакетов.
      dtx: true,
      red: true,
    },

    disconnectOnPageLeave: true,
  });
}

/**
 * Настройки захвата экрана.
 *
 * ── Почему по умолчанию предлагается весь экран ─────────────────────
 *
 * Захват отдельного окна ломается ровно в тот момент, когда он нужнее
 * всего — когда приложение разворачивают на весь экран. В macOS такое
 * окно уезжает в отдельное пространство (Space), и захватчик продолжает
 * снимать прежний идентификатор окна: собеседники видят застывший кадр
 * или чёрный прямоугольник. В Windows то же самое происходит с
 * полноэкранными режимами PowerPoint и просмотрщиков PDF — показ
 * создаёт новое окно поверх, а снимается по-прежнему старое.
 *
 * Захват экрана целиком от этого не страдает: снимается дисплей, а не
 * окно, и что бы на нём ни разворачивали — попадёт в кадр. Поэтому
 * `displaySurface: 'monitor'` открывает системный диалог сразу на нужной
 * вкладке. Запретить выбор окна браузер не даёт, поэтому вторая половина
 * решения — подсказка `screenShareHint` из `roomState.ts`.
 *
 * ── Про `contentHint` ───────────────────────────────────────────────
 *
 * Его здесь намеренно нет. Подсказка `'detail'` выглядела уместной —
 * на экране текст, — но она переводит дорожку в режим «держать
 * разрешение любой ценой». На прокрутке документа кодировщику не
 * хватает битрейта, он оставляет 1080p и роняет частоту кадров до
 * нескольких: показ начинает дёргаться. Без подсказки браузер выбирает
 * сбалансированное поведение — ровно то, что нужно.
 *
 * Остальное:
 *   · `resolution` — потолок съёмки. 1080p при 30 кадрах: монитор может
 *     отдавать 60, и тогда половина работы кодировщика уходит на кадры,
 *     которые всё равно не поместятся в битрейт;
 *   · `selfBrowserSurface: 'exclude'` — не предлагать вкладку самой
 *     встречи, иначе получается зеркальный коридор;
 *   · `surfaceSwitching: 'include'` — источник можно сменить прямо во
 *     время показа, не начиная заново;
 *   · `systemAudio: 'include'` — можно поделиться звуком видео.
 */
export const SCREEN_SHARE_OPTIONS = {
  audio: true,
  video: { displaySurface: 'monitor' as const },
  resolution: { width: 1920, height: 1080, frameRate: 30 },
  selfBrowserSurface: 'exclude' as const,
  surfaceSwitching: 'include' as const,
  systemAudio: 'include' as const,
};

/** События, после которых нужно перерисовать интерфейс встречи. */
export const ROOM_UPDATE_EVENTS: RoomEvent[] = [
  RoomEvent.ParticipantConnected,
  RoomEvent.ParticipantDisconnected,
  RoomEvent.TrackSubscribed,
  RoomEvent.TrackUnsubscribed,
  RoomEvent.TrackPublished,
  RoomEvent.TrackUnpublished,
  RoomEvent.LocalTrackPublished,
  RoomEvent.LocalTrackUnpublished,
  RoomEvent.TrackMuted,
  RoomEvent.TrackUnmuted,
  RoomEvent.ActiveSpeakersChanged,
  RoomEvent.ConnectionQualityChanged,
  RoomEvent.ConnectionStateChanged,
  RoomEvent.ParticipantNameChanged,
  RoomEvent.MediaDevicesError,
  // Метаданные участника несут признак организатора: без этого события
  // тот, кто вошёл раньше организатора, не узнал бы, что тот появился.
  RoomEvent.ParticipantMetadataChanged,
];

/** Организатор ли участник — признак лежит в его метаданных. */
export const isHostParticipant = (p: Participant): boolean => readHostFlag(p.metadata);

export interface ParticipantView {
  sid: string;
  identity: string;
  name: string;
  isLocal: boolean;
  isHost: boolean;
  isSpeaking: boolean;
  micEnabled: boolean;
  cameraEnabled: boolean;
  screenShareEnabled: boolean;
  connectionQuality: ConnectionQuality;
  cameraPublication?: TrackPublication;
  screenPublication?: TrackPublication;
  /**
   * Живой объект участника. Нужен индикатору громкости: `audioLevel`
   * меняется десятки раз в секунду, и гонять такие обновления через
   * состояние React — верный способ положить интерфейс. Индикатор
   * читает его сам в кадре анимации.
   */
  raw: Participant;
}

function toView(p: Participant, isLocal: boolean): ParticipantView {
  const camera = p.getTrackPublication(Track.Source.Camera);
  const screen = p.getTrackPublication(Track.Source.ScreenShare);

  return {
    sid: p.sid,
    identity: p.identity,
    name: p.name?.trim() || p.identity,
    isLocal,
    isHost: isHostParticipant(p),
    isSpeaking: p.isSpeaking,
    micEnabled: p.isMicrophoneEnabled,
    cameraEnabled: Boolean(camera && !camera.isMuted && camera.track),
    screenShareEnabled: Boolean(screen && !screen.isMuted && screen.track),
    connectionQuality: p.connectionQuality,
    cameraPublication: camera,
    screenPublication: screen,
    raw: p,
  };
}

/** Локальный участник всегда первым, остальные — в порядке подключения. */
export function collectParticipants(room: Room): ParticipantView[] {
  const local = toView(room.localParticipant as LocalParticipant, true);
  const remote = Array.from(room.remoteParticipants.values()).map((p: RemoteParticipant) =>
    toView(p, false),
  );
  return [local, ...remote];
}

// ── Чат поверх data-канала LiveKit ────────────────────────────────────

export const CHAT_TOPIC = 'gto.chat';

export interface ChatMessage {
  id: string;
  from: string;
  text: string;
  at: number;
  own: boolean;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export async function sendChatMessage(room: Room, text: string): Promise<void> {
  const payload = { text, at: Date.now() };
  await room.localParticipant.publishData(encoder.encode(JSON.stringify(payload)), {
    reliable: true,
    topic: CHAT_TOPIC,
  });
}

/** Верхняя граница длины входящего сообщения. */
const MAX_CHAT_LENGTH = 2000;

export function decodeChatMessage(
  payload: Uint8Array,
  participant: RemoteParticipant | undefined,
): ChatMessage | null {
  try {
    const data = JSON.parse(decoder.decode(payload));
    if (typeof data.text !== 'string' || !data.text.trim()) return null;

    return {
      id: `${participant?.sid ?? 'unknown'}-${data.at}-${Math.random().toString(36).slice(2, 7)}`,
      from: participant?.name?.trim() || participant?.identity || 'Участник',
      // Отправитель — тоже клиент, доверять его ограничениям нельзя:
      // подрезаем длину сами, иначе одно сообщение может подвесить панель.
      text: data.text.slice(0, MAX_CHAT_LENGTH),
      at: typeof data.at === 'number' ? data.at : Date.now(),
      own: false,
    };
  } catch {
    return null;
  }
}

export const QUALITY_LABEL: Record<ConnectionQuality, string> = {
  [ConnectionQuality.Excellent]: 'Отличное соединение',
  [ConnectionQuality.Good]: 'Хорошее соединение',
  [ConnectionQuality.Poor]: 'Слабое соединение',
  [ConnectionQuality.Lost]: 'Соединение потеряно',
  [ConnectionQuality.Unknown]: 'Соединение проверяется',
};
